import { useTranslation } from "react-i18next";

import type { PublicTenantBranding } from "@/api/queries/branding";
import { DEFAULT_LANG, isSupportedLang } from "@/i18n";
import { cx } from "@/lib/utils";
import { buildPlatformUrl } from "@/shell/resolveShell";

interface TenantBrandMarkProps {
  branding?: Pick<PublicTenantBranding, "name" | "logo_url"> | null;
  showPoweredBy?: boolean;
  className?: string;
  logoClassName?: string;
  logoTestId?: string;
  nameTestId?: string;
}

export function TenantBrandMark({
  branding,
  showPoweredBy = false,
  className,
  logoClassName,
  logoTestId = "tenant-brand-logo",
  nameTestId = "tenant-brand-name",
}: TenantBrandMarkProps): JSX.Element {
  const { t, i18n } = useTranslation("common");
  const name = branding?.name?.trim();
  const logoUrl = branding?.logo_url?.trim();

  // The attribution pill is the only route from a tenant storefront to
  // the platform's own site, so it has to be a link — it read as one
  // (bordered pill next to the logo) long before it was one.
  //
  // ``buildPlatformUrl`` rather than a literal domain: apex, scheme and
  // port come from the running deployment, so a click on a dev server
  // opens the local marketing shell instead of production. Language
  // comes from i18next (App's ``/:lang`` gate pushes the route segment
  // into it), which keeps this component out of the router.
  const lang = isSupportedLang(i18n.language) ? i18n.language : DEFAULT_LANG;

  return (
    <span className={cx("inline-flex items-center gap-2", className)}>
      {logoUrl ? (
        <img
          src={logoUrl}
          alt={name ?? ""}
          className={cx("h-7 max-w-[160px] object-contain", logoClassName)}
          data-testid={logoTestId}
        />
      ) : name ? (
        <span
          className="text-base font-semibold tracking-tight text-ink"
          data-testid={nameTestId}
        >
          {name}
        </span>
      ) : // KAN-1644 — obchod bez jména firmy a bez loga nenese nic: logo
      // platformy by vydávalo obchod za platformu (údaje tenanta nikdy
      // nepadají na platformní — raději prázdné).
      null}
      {showPoweredBy && (
        <a
          href={buildPlatformUrl(`/${lang}`)}
          target="_blank"
          rel="noreferrer"
          className="rounded-full border border-border bg-surface px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.06em] text-ink-3"
          data-testid="tenant-powered-by"
        >
          {t("footer.poweredBy", { defaultValue: "Powered by Forgmatic" })}
        </a>
      )}
    </span>
  );
}
